import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowDownAZ,
  ArrowUpAZ,
  Bell,
  ChevronDown,
  Clock,
  Coins,
  Globe,
  KeyRound,
  LogOut,
  Moon,
  Play,
  RefreshCw,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';
import type { KeepaFilters, KeepaQueryTemplate, TemplateParameterSpec } from '@sourcito/schemas';
import { Badge, Button, Card, useTheme } from '../design';
import { useMe, useSignout } from '../lib/auth';
import { useCredentials, useKeepaBalance, useTavilyBalance } from '../lib/credentials';
import {
  fetchBrandSizes,
  previewNlSearch,
  streamNlSearch,
  type NlSearchEvent,
} from '../lib/nlSearch';
import {
  useKeepaTemplates,
  useRunKeepaTemplate,
  useTemplateDiscoveredProducts,
} from '../lib/keepaTemplates';
import { useCreateKeepaSearchRun, useKeepaBudget } from '../lib/keepaSearchRuns';
import { useActiveSchedules } from '../lib/activeSchedules';
import { useReviewQueueCount } from '../lib/products';
import { useProductFlagsCount } from '../lib/productFlags';
import {
  TemplateParameterForm,
  type TemplateParameterValues,
} from '../components/TemplateParameterForm';
import { ActiveCrawlPanel } from '../components/ActiveCrawlPanel';
import { BrandSummaryDialog } from '../components/BrandSummaryDialog';
import { RunActionButton } from '../components/RunActionButton';
import { SaveQueryDialog } from '../components/SaveQueryDialog';

type BrandSizeBucket = 'micro' | 'small' | 'medium' | 'large';
type BrandSizeStatus = BrandSizeBucket | 'pending';

type Phase =
  | 'idle'
  | 'previewing' // LLM is translating; no Keepa cost yet
  | 'previewed' // translation done; user has not clicked Run
  | 'searching'
  | 'scoring'
  | 'done'
  | 'error';

interface ScoredRow {
  id: string;
  asin: string;
  title: string | null;
  brand: string | null;
  category: string | null;
  currentPriceCents: number | null;
  currentBsr: number | null;
  monthlySalesEstimate: number | null;
  reviewCount: number | null;
  rating: number | null;
  estimatedRoiPct: number | null;
  roiGrade: 'A' | 'B' | 'C' | 'D' | 'F' | null;
  isNew?: boolean;
  /** Annotation from keepa_brand_cache. 'pending' means background probe in flight. */
  brandSize?: BrandSizeStatus | null;
}

type SortField = 'roi' | 'bsr' | 'price' | 'sales';
type SortDir = 'asc' | 'desc';

export function SearchPage() {
  const { data: me } = useMe();
  const { data: credentials } = useCredentials();
  const { theme, setTheme } = useTheme();
  const signout = useSignout();

  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [filters, setFilters] = useState<KeepaFilters | null>(null);
  const [rationale, setRationale] = useState<string | null>(null);
  const [estimatedTokens, setEstimatedTokens] = useState<number | null>(null);
  const [tierHint, setTierHint] = useState<'free' | 'paid' | 'unknown'>('unknown');
  const [products, setProducts] = useState<ScoredRow[]>([]);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const [brandSizeFilteredCount, setBrandSizeFilteredCount] = useState(0);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: 'roi',
    dir: 'desc',
  });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Pass 5 — template picker + queue/now toggle. When a template is selected,
  // the freeform NL input is hidden and the parameter form drives the request.
  // The URL ?templateId=<id> preselects a template (used by /templates → Run).
  const initialTemplateId = (() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('templateId') ?? '';
  })();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId);
  const [parameterValues, setParameterValues] = useState<TemplateParameterValues>({});
  const [runMode, setRunMode] = useState<'now' | 'queued'>('now');
  const [queueToast, setQueueToast] = useState<string | null>(null);
  // Phase 1.9 Pass 1 — state D ("results, saved"). Set when the operator clicks
  // Save query on a free-text result set. Distinct from `selectedTemplate`,
  // which is picker-driven. Either being non-null means we're in state D.
  const [savedTemplate, setSavedTemplate] = useState<KeepaQueryTemplate | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  const balance = useKeepaBalance();
  const tavilyBalance = useTavilyBalance();
  const templates = useKeepaTemplates();
  const selectedTemplate =
    selectedTemplateId !== ''
      ? (templates.data?.find((t) => t.id === selectedTemplateId) ?? null)
      : null;
  const runTemplate = useRunKeepaTemplate();
  const createSearchRun = useCreateKeepaSearchRun();
  const budget = useKeepaBudget();
  const reviewQueue = useReviewQueueCount();
  const inboxCount = useProductFlagsCount({ decision: 'pending' });
  // Drives the disabled state of the "Save & start hourly" preview-card
  // action. The server hard-caps at 1 enabled hourly per tenant (409
  // HOURLY_LIMIT_REACHED) — surfacing it here lets us disable the button
  // with a tooltip BEFORE the user clicks. See
  // docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §4.
  const activeSchedules = useActiveSchedules();
  const activeHourly = activeSchedules.data?.schedules.find(
    (s) => s.cronExpression === '0 * * * *' && s.enabled
  );
  const hasActiveHourly = Boolean(activeHourly);
  const activeHourlyName = activeHourly?.name ?? null;

  // Track pending brand-size polls so they can be cancelled on unmount or
  // when a new search supersedes the previous one's hydration window.
  const pollTimers = useRef<number[]>([]);
  useEffect(
    () => () => {
      pollTimers.current.forEach((t) => window.clearTimeout(t));
      pollTimers.current = [];
    },
    []
  );

  function schedulePendingBrandPolls(rows: ScoredRow[]) {
    // Cancel any leftover polls from a previous search.
    pollTimers.current.forEach((t) => window.clearTimeout(t));
    pollTimers.current = [];

    const pendingBrands = Array.from(
      new Set(
        rows.filter((r) => r.brandSize === 'pending' && r.brand).map((r) => r.brand as string)
      )
    );
    if (pendingBrands.length === 0) return;

    // 3s / 6s / 12s / 24s — geometric backoff. Most brand probes complete
    // in under 5s; the long tail catches anything held up by Keepa pacing.
    const schedule = [3_000, 6_000, 12_000, 24_000];
    for (const delay of schedule) {
      const handle = window.setTimeout(async () => {
        try {
          const map = await fetchBrandSizes(pendingBrands);
          setProducts((prev) =>
            prev.map((p) => {
              if (!p.brand) return p;
              const next = map[p.brand];
              if (!next) return p;
              if (p.brandSize === next) return p;
              return { ...p, brandSize: next };
            })
          );
        } catch {
          // Ignore — polling is best-effort.
        }
      }, delay);
      pollTimers.current.push(handle);
    }
  }

  const hasKeepaKey = credentials?.some(
    (c) => c.provider === 'keepa' && c.validationStatus !== 'invalid'
  );
  const hasOpenRouterKey = credentials?.some(
    (c) => c.provider === 'openrouter' && c.validationStatus !== 'invalid'
  );
  const ready = hasKeepaKey && hasOpenRouterKey;

  async function runPreview() {
    const trimmed = query.trim();
    if (!trimmed) return;
    setPhase('previewing');
    setProducts([]);
    setError(null);
    setFilters(null);
    setRationale(null);
    setEstimatedTokens(null);
    try {
      const preview = await previewNlSearch({ query: trimmed });
      setFilters(preview.translatedFilters);
      setRationale(preview.translatedFilters.rationale ?? null);
      setEstimatedTokens(preview.estimatedKeepaTokens);
      setTierHint(preview.tierHint);
      setPhase('previewed');
    } catch (err) {
      setError({ code: 'PREVIEW_FAILED', message: (err as Error).message });
      setPhase('error');
    }
  }

  async function runSearch(body: { query?: string; filters?: KeepaFilters }) {
    setPhase(body.query ? 'previewing' : 'searching');
    setProducts([]);
    setTotalResults(null);
    setError(null);
    if (body.filters) setFilters(body.filters);

    try {
      for await (const evt of streamNlSearch(body)) {
        applyEvent(evt);
      }
    } catch (err) {
      setError({ code: 'STREAM_ERROR', message: (err as Error).message });
      setPhase('error');
    } finally {
      // Refresh balance after every search since tokens were burned.
      balance.refetch();
    }
  }

  function applyEvent(evt: NlSearchEvent) {
    switch (evt.event) {
      case 'filters':
        setFilters(evt.data);
        setRationale(evt.data.rationale ?? null);
        setPhase('searching');
        break;
      case 'progress':
        if (evt.data.stage === 'scoring') setPhase('scoring');
        break;
      case 'result':
        setProducts(evt.data.products as ScoredRow[]);
        setTotalResults(evt.data.totalResults);
        setBrandSizeFilteredCount(evt.data.brandSizeFilteredCount ?? 0);
        setPhase('done');
        // Kick off badge-hydration polling for any 'pending' brands. The
        // server's background probes will populate keepa_brand_cache; we
        // re-query the cheap brand-sizes endpoint a few times until either
        // all badges resolve or we hit the timeout.
        schedulePendingBrandPolls(evt.data.products as ScoredRow[]);
        break;
      case 'error':
        setError(evt.data);
        setPhase('error');
        break;
    }
  }

  function confirmRun() {
    if (!filters) return;
    runSearch({ filters });
  }

  // The mode arg is now passed explicitly by the RunActionButton — keeps
  // the function pure of the runMode-state read so we don't depend on
  // React state batching when the button calls us inline.
  async function runTemplateAction(mode: 'now' | 'queued' = runMode) {
    if (!selectedTemplate) return;
    setQueueToast(null);
    setSavedToast(null);

    // Phase 1.9 Pass 2 — saved-from-search templates (parameters: []) replay
    // their frozen filters inline so the operator lands in state D with
    // results visible. Parameter-bearing templates keep the queued behavior
    // (multi-field forms don't fit the SSE stream UX).
    const isInlineReplay = selectedTemplate.parameters.length === 0 && mode === 'now';

    if (isInlineReplay) {
      try {
        await runSearch({ filters: selectedTemplate.fixedFilters as KeepaFilters });
      } catch (err) {
        setError({ code: 'TEMPLATE_RUN_FAILED', message: (err as Error).message });
      }
      return;
    }

    try {
      const run = await runTemplate.mutateAsync({
        id: selectedTemplate.id,
        input: {
          parameterValues: parameterValues as Record<string, unknown>,
          runMode: mode,
        },
      });
      setQueueToast(
        `Template queued — run id ${run.id.slice(0, 8)}. Watch progress in Run history.`
      );
    } catch (err) {
      setError({ code: 'TEMPLATE_RUN_FAILED', message: (err as Error).message });
    }
  }

  async function queueFreeformAction() {
    if (!filters) return;
    setQueueToast(null);
    try {
      const run = await createSearchRun.mutateAsync({
        filters,
        runMode: 'queued',
      });
      setQueueToast(`Search queued — run id ${run.id.slice(0, 8)}. Watch progress in Run history.`);
      reset();
    } catch (err) {
      setError({ code: 'QUEUE_FAILED', message: (err as Error).message });
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim() || !ready) return;
    runPreview();
  }

  function removeFilter(key: keyof KeepaFilters) {
    if (!filters) return;
    const next = { ...filters };
    delete next[key];
    runSearch({ filters: next });
  }

  function reset() {
    setPhase('idle');
    setFilters(null);
    setRationale(null);
    setEstimatedTokens(null);
    setError(null);
    setProducts([]);
    setTotalResults(null);
    setSavedTemplate(null);
    setSavedToast(null);
  }

  const sorted = [...products].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (p: ScoredRow): number | null => {
      if (sort.field === 'roi') return p.estimatedRoiPct;
      if (sort.field === 'bsr') return p.currentBsr;
      if (sort.field === 'price') return p.currentPriceCents;
      return p.monthlySalesEstimate;
    };
    const av = get(a) ?? Number.NEGATIVE_INFINITY;
    const bv = get(b) ?? Number.NEGATIVE_INFINITY;
    return (av - bv) * dir;
  });

  function setSortField(field: SortField) {
    setSort((s) =>
      s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }
    );
  }

  const busy = phase === 'previewing' || phase === 'searching' || phase === 'scoring';

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-medium leading-tight">Sourcito</h1>
            <p className="text-xs text-fg-tertiary">Source smarter. Own your stack.</p>
          </div>
          <div className="flex items-center gap-2 relative">
            {(reviewQueue.data?.count ?? 0) > 0 && (
              <a
                href={`/products?status=pending&sortBy=recent`}
                className="inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1 text-xs font-medium tabular-nums bg-accent/[0.12] text-accent-fg hover:bg-accent/[0.18] transition-colors duration-fast"
                title="Click to review newly-discovered ASINs"
              >
                <Bell className="size-3.5" />
                {reviewQueue.data!.count} new
                <span className="text-accent-fg/70">to review</span>
              </a>
            )}
            {(inboxCount.data?.count ?? 0) > 0 && (
              <Link
                to="/inbox"
                className="inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1 text-xs font-medium tabular-nums bg-warning/[0.12] text-warning hover:bg-warning/[0.2] transition-colors duration-fast"
                title="Click to triage flagged products in the inbox"
              >
                <Bell className="size-3.5" />
                {inboxCount.data!.count}
                <span className="text-warning/70">flagged</span>
              </Link>
            )}
            <KeepaBalanceChip
              tokensLeft={balance.data?.tokensLeft ?? null}
              refillRate={balance.data?.refillRate ?? null}
              loading={balance.isFetching}
              onRefresh={() => balance.refetch()}
            />
            {tavilyBalance.data && (
              <TavilyBalanceChip
                usage={tavilyBalance.data.usage}
                limit={tavilyBalance.data.limit}
                remaining={tavilyBalance.data.remaining}
                plan={tavilyBalance.data.plan}
                loading={tavilyBalance.isFetching}
                onRefresh={() => tavilyBalance.refetch()}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === 'warm-dark' ? 'warm-light' : 'warm-dark')}
            >
              {theme === 'warm-dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              trailingIcon={<ChevronDown className="size-4" />}
              onClick={() => setSettingsOpen((v) => !v)}
            >
              Settings
            </Button>
            {settingsOpen && (
              <div
                className="absolute right-0 top-full mt-2 z-10 bg-surface-elev border border-border-default rounded-md shadow-lg min-w-48 py-1"
                onMouseLeave={() => setSettingsOpen(false)}
              >
                <DropdownLink to="/inbox" label="Inbox" subtitle="Indie candidates to triage" />
                <DropdownLink to="/products" label="Products" subtitle="Discovered ASINs" />
                <DropdownLink to="/brands" label="Brands" subtitle="Curate selected / rejected" />
                <DropdownLink
                  to="/sourcing"
                  label="Sourcing"
                  subtitle="Selected brands + pipeline"
                />
                <DropdownLink to="/suppliers" label="Suppliers" subtitle="Pipeline" />
                <DropdownLink to="/runs" label="Run history" subtitle="Keepa search runs" />
                <DropdownLink to="/schedules" label="Schedules" subtitle="Recurring runs" />
                <DropdownLink to="/templates" label="Templates" subtitle="Saved query presets" />
                <DropdownLink to="/credentials" label="Credentials" subtitle={me?.user.email} />
                <DropdownLink
                  to="/settings/sp-api"
                  label="Connect SP-API"
                  subtitle="Gating + exact fees"
                />
                <DropdownLink to="/fee-profiles" label="Fee profiles" />
                <DropdownLink to="/audit-logs" label="Audit log" subtitle="Search history" />
                <DropdownLink to="/design" label="Design system" />
                <div className="border-t border-border-subtle my-1" />
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-fg-primary hover:bg-surface flex items-center gap-2"
                  onClick={() => signout.mutate()}
                >
                  <LogOut className="size-4 text-fg-secondary" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10 space-y-6">
        {/*
         * Active Crawl panel — visible only when the tenant has at least
         * one enabled schedule. Renders nothing otherwise (returns null
         * internally) so users without recurring crawls see a clean page.
         * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §1.
         */}
        <ActiveCrawlPanel />
        {!ready && (
          <Card className="border-warning/30 bg-warning/[0.06]">
            <div className="flex items-start gap-3">
              <KeyRound className="size-5 text-warning shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-fg-primary">Credentials needed</p>
                <p className="text-sm text-fg-secondary mt-1">
                  You need both Keepa (data source) and OpenRouter (query translation, BYO model)
                  keys to search. {!hasKeepaKey && <span>Keepa is missing.</span>}{' '}
                  {!hasOpenRouterKey && <span>OpenRouter is missing.</span>}
                </p>
                <Link
                  to="/credentials"
                  className="text-sm font-medium text-accent-fg hover:underline mt-2 inline-block"
                >
                  Add credentials →
                </Link>
              </div>
            </div>
          </Card>
        )}

        {queueToast && (
          <Card className="border-accent/30 bg-accent/[0.06]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-accent-fg">{queueToast}</p>
              <div className="flex items-center gap-2">
                <a href="/runs" className="text-sm font-medium text-accent-fg hover:underline">
                  View runs →
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Dismiss"
                  onClick={() => setQueueToast(null)}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        <TemplatePicker
          templates={templates.data ?? []}
          selectedId={selectedTemplateId}
          onChange={(id) => {
            setSelectedTemplateId(id);
            setParameterValues({});
            setQueueToast(null);
            setSavedTemplate(null);
            setSavedToast(null);
          }}
          loading={templates.isLoading}
        />

        {selectedTemplate ? (
          <Card>
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-fg-tertiary">
                    Template parameters
                  </p>
                  {selectedTemplate.description && (
                    <p className="text-xs text-fg-secondary mt-1 italic">
                      {selectedTemplate.description}
                    </p>
                  )}
                </div>
              </div>
              <TemplateParameterForm
                parameters={selectedTemplate.parameters as TemplateParameterSpec[]}
                values={parameterValues}
                onChange={setParameterValues}
                idPrefix="search-tpl"
              />
              {budget.data && !budget.data.ok && <BudgetWarning budget={budget.data} />}
              <div className="flex items-center gap-2">
                <RunActionButton
                  onRunNow={() => runTemplateAction('now')}
                  onQueue={() => runTemplateAction('queued')}
                  loading={runTemplate.isPending}
                  disabled={!ready}
                  runLabel="Run template now"
                  queueLabel="Queue template for later"
                />
                <p className="text-xs text-fg-tertiary">
                  Either path creates a row in{' '}
                  <Link to="/runs" className="text-accent-fg hover:underline">
                    Run history
                  </Link>
                  . "Run now" fires inline when budget allows; "Queue" always defers to the next
                  cron tick.
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <QueryTextarea
              value={query}
              onChange={setQuery}
              disabled={busy || !ready}
              onSubmit={() => runPreview()}
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-fg-tertiary px-1">
                OpenRouter parses your query → preview filters & token cost. The preview card has
                all four next-step actions (Run, Queue, Save, Save&Schedule).{' '}
                <span className="text-fg-tertiary/70">
                  Enter to preview, Shift+Enter for newline.
                </span>
              </p>
              <Button
                type="submit"
                size="md"
                loading={phase === 'previewing'}
                disabled={!query.trim() || !ready || busy}
                leadingIcon={<Sparkles className="size-4" />}
              >
                Preview
              </Button>
            </div>
            {/*
              Run/Queue/Save actions live in the PreviewConfirmCard below — see
              docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3.
              Removed in this redesign: the in-row RunModeToggle and the
              conditional Queue button that appeared after preview.
            */}
          </form>
        )}

        {phase === 'previewed' && filters && estimatedTokens !== null && (
          <PreviewConfirmCard
            filters={filters}
            rationale={rationale}
            estimatedTokens={estimatedTokens}
            balance={balance.data?.tokensLeft ?? null}
            tierHint={tierHint}
            onRun={confirmRun}
            onQueue={queueFreeformAction}
            onCancel={reset}
            // PreviewConfirmCard only renders while phase === 'previewed';
            // clicking Run transitions phase past this state and the card
            // unmounts — so the button doesn't need its own spinner.
            queuePending={createSearchRun.isPending}
            onRemoveFilter={(key) => {
              const next = { ...filters };
              delete next[key];
              setFilters(next);
              setEstimatedTokens(estimateClientCost(next));
            }}
            saveAction={
              !savedTemplate && !selectedTemplate ? (
                // Preview-time Save — gives the operator a zero-Keepa-cost way
                // to bookmark the query before they spend tokens running it.
                // Hidden once a template is already attached (savedTemplate set
                // after save; selectedTemplate set if the user navigated from
                // /templates).
                <SaveQueryDialog
                  filters={filters}
                  initialName={query}
                  naturalLanguageQuery={query}
                  onSaved={(tpl) => {
                    setSavedTemplate(tpl);
                    setSavedToast(`Saved as "${tpl.name}". Manage from /templates anytime.`);
                  }}
                />
              ) : undefined
            }
            saveAndScheduleAction={
              !savedTemplate && !selectedTemplate ? (
                // Combined save + hourly schedule via the /with-schedule
                // endpoint. Disable when the tenant already has an enabled
                // hourly — the server will 409 either way, but disabling
                // gives an inline explanation before the user clicks.
                <SaveQueryDialog
                  filters={filters}
                  initialName={query}
                  naturalLanguageQuery={query}
                  withSchedule
                  disabled={hasActiveHourly}
                  disabledReason={
                    hasActiveHourly
                      ? `You already have an enabled hourly crawl ("${activeHourlyName ?? 'unnamed'}"). Pause it in /schedules or use Save as template + a different cadence.`
                      : undefined
                  }
                  onSaved={(tpl) => {
                    setSavedTemplate(tpl);
                  }}
                  onScheduled={(sched) => {
                    setSavedToast(
                      `Crawling hourly. Next fire at the top of the hour. Manage in /schedules ("${sched.name}").`
                    );
                  }}
                />
              ) : undefined
            }
          />
        )}

        {(phase === 'searching' || phase === 'scoring' || phase === 'done') && filters && (
          <FilterChips filters={filters} rationale={rationale} onRemove={removeFilter} />
        )}

        {/*
         * Save toast. Replaces both the Phase 1.9 "Save query" toolbar
         * (results-page button) and the bigger post-save accent banner
         * (with Start hourly crawl + Run again). Both were redundant
         * surfaces — Save lives in the preview card only now, and live
         * crawl state lives in ActiveCrawlPanel. The toast is just a
         * post-action acknowledgement.
         * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §5.
         */}
        {savedToast && (
          <Card className="border-positive/30 bg-positive/[0.06]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-positive">{savedToast}</p>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Dismiss"
                onClick={() => setSavedToast(null)}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          </Card>
        )}

        {phase === 'previewing' && (
          <p className="text-sm text-fg-secondary">Translating your query…</p>
        )}
        {phase === 'searching' && <p className="text-sm text-fg-secondary">Fetching from Keepa…</p>}
        {phase === 'scoring' && (
          <p className="text-sm text-fg-secondary">Scoring with your fee profile…</p>
        )}

        {error && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm font-medium text-negative">Search failed: {error.code}</p>
            <p className="text-sm text-fg-secondary mt-1">{error.message}</p>
          </Card>
        )}

        {phase === 'done' && products.length === 0 && filters && (
          <EmptyResultDiagnostic
            filters={filters}
            rationale={rationale}
            totalResults={totalResults}
            brandSizeFilteredCount={brandSizeFilteredCount}
            onRemove={removeFilter}
          />
        )}

        {sorted.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-4 py-2.5 font-medium">ASIN</th>
                  <th className="text-left px-4 py-2.5 font-medium">Title</th>
                  <th className="text-left px-4 py-2.5 font-medium">Brand</th>
                  <th className="text-left px-4 py-2.5 font-medium">Size</th>
                  <SortHeader label="BSR" field="bsr" sort={sort} onClick={setSortField} />
                  <SortHeader
                    label="Price"
                    field="price"
                    sort={sort}
                    onClick={setSortField}
                    align="right"
                  />
                  <SortHeader
                    label="Sales/mo"
                    field="sales"
                    sort={sort}
                    onClick={setSortField}
                    align="right"
                  />
                  <SortHeader
                    label="ROI"
                    field="roi"
                    sort={sort}
                    onClick={setSortField}
                    align="right"
                  />
                  <th className="text-center px-4 py-2.5 font-medium">Grade</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.id} className="border-t border-border-subtle hover:bg-surface-elev">
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <a
                        href={`https://www.amazon.com/dp/${p.asin}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {p.asin}
                      </a>
                      {p.isNew && (
                        <Badge variant="accent" className="ml-2 text-xs">
                          New
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 max-w-md truncate">{p.title ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      <BrandChip brand={p.brand} />
                    </td>
                    <td className="px-4 py-2.5">
                      <BrandSizeBadge status={p.brandSize ?? null} />
                    </td>
                    <td className="px-4 py-2.5">
                      {p.currentBsr === null ? '—' : p.currentBsr.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {p.currentPriceCents === null
                        ? '—'
                        : `$${(p.currentPriceCents / 100).toFixed(2)}`}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {p.monthlySalesEstimate === null
                        ? '—'
                        : p.monthlySalesEstimate.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {p.estimatedRoiPct === null
                        ? '—'
                        : `${(p.estimatedRoiPct * 100).toFixed(0)}%`}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {p.roiGrade ? <GradeBadge grade={p.roiGrade} /> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Phase 1.9 Pass 3 — All-discovered accordion. Visible in state D. */}
        {(savedTemplate || selectedTemplate) && phase === 'done' && (
          <DiscoveredByTemplateAccordion
            template={(savedTemplate ?? selectedTemplate)!}
            currentRunAsins={new Set(sorted.map((p) => p.asin))}
          />
        )}

        {phase === 'idle' && !filters && (
          <Card className="border-dashed py-10 text-center">
            <p className="text-sm text-fg-secondary">
              Type a natural-language product query above. AI translates it into Keepa filters, you
              preview the cost, then click Run to fetch + ROI-score.
            </p>
            <p className="text-xs text-fg-tertiary mt-3">
              Try: <em>kitchen gadgets under $50, BSR under 50K, fewer than 200 reviews</em>
            </p>
          </Card>
        )}
      </main>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function TemplatePicker({
  templates,
  selectedId,
  onChange,
  loading,
}: {
  templates: KeepaQueryTemplate[];
  selectedId: string;
  onChange: (id: string) => void;
  loading: boolean;
}) {
  const [filter, setFilter] = useState('');
  const trimmed = filter.trim().toLowerCase();
  // Sort by most-recently-updated so daily-driver queries float to the top.
  const sorted = [...templates].sort((a, b) =>
    (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  );
  const filtered = trimmed
    ? sorted.filter((t) => {
        return (
          t.name.toLowerCase().includes(trimmed) ||
          (t.description ?? '').toLowerCase().includes(trimmed)
        );
      })
    : sorted;
  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label
            htmlFor="template-picker"
            className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5"
          >
            Template
          </label>
          <select
            id="template-picker"
            value={selectedId}
            onChange={(e) => onChange(e.target.value)}
            disabled={loading}
            className="w-full h-10 px-3 bg-surface-elev border border-border-default rounded-sm text-base"
            title={
              selected?.description ? `${selected.name} — ${selected.description}` : selected?.name
            }
          >
            <option value="">(No template — type freeform query below)</option>
            {filtered.map((t) => (
              <option key={t.id} value={t.id} title={summarizeFilters(t.fixedFilters)}>
                {t.name}
                {t.origin === 'builtin' ? ' · built-in' : ''}
                {t.parameters.length === 0 ? '' : ` · ${t.parameters.length} param(s)`}
              </option>
            ))}
            {filtered.length === 0 && trimmed.length > 0 && (
              <option disabled value="">
                — no templates match "{filter}" —
              </option>
            )}
          </select>
        </div>
        <a
          href="/templates"
          className="text-xs font-medium text-accent-fg hover:underline whitespace-nowrap mb-3"
        >
          Manage templates →
        </a>
      </div>

      {templates.length > 5 && (
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${templates.length} templates by name or description…`}
          className="w-full h-8 px-3 bg-surface-elev/50 border border-border-subtle rounded-xs text-sm placeholder:text-fg-tertiary focus:outline-none focus:border-accent transition-colors duration-fast"
        />
      )}

      {selected?.description && (
        <p className="text-xs text-fg-secondary italic px-1">{selected.description}</p>
      )}
    </div>
  );
}

/**
 * One-line filter summary for the option tooltip — e.g.
 * "BSR ≤ 50k · price ≤ $25 · reviews ≤ 100". Keeps it tight so screen-reader
 * users get the gist without 6 lines of JSON.
 */
function summarizeFilters(filters: Record<string, unknown>): string {
  const parts: string[] = [];
  const bsr = (filters.bsrCurrent ?? null) as { max?: number; min?: number } | null;
  if (bsr?.max !== undefined) parts.push(`BSR ≤ ${bsr.max.toLocaleString()}`);
  const price = (filters.amazonPriceCurrent ?? null) as { max?: number; min?: number } | null;
  if (price?.max !== undefined) parts.push(`price ≤ $${Math.round(price.max / 100)}`);
  if (price?.min !== undefined) parts.push(`price ≥ $${Math.round(price.min / 100)}`);
  const reviews = (filters.reviewCount ?? null) as { max?: number; min?: number } | null;
  if (reviews?.max !== undefined) parts.push(`reviews ≤ ${reviews.max}`);
  const brand = filters.brand as string[] | undefined;
  if (brand?.length) parts.push(`brand: ${brand.slice(0, 2).join(', ')}`);
  const cat = filters.category as string | undefined;
  if (cat) parts.push(`cat: ${cat}`);
  return parts.length > 0 ? parts.join(' · ') : 'frozen filter set';
}

function BudgetWarning({
  budget,
}: {
  budget: {
    deficit: number;
    nextEligibleAt: string | null;
    hourlyBudget: number;
    tokensUsedLast60Min: number;
  };
}) {
  const eta = budget.nextEligibleAt ? new Date(budget.nextEligibleAt) : null;
  const minutesUntil = eta ? Math.max(1, Math.round((eta.getTime() - Date.now()) / 60_000)) : null;

  return (
    <Card className="border-warning/30 bg-warning/[0.06]">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="size-4 text-warning shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-fg-primary">
            Insufficient Keepa budget — switch to <strong>Queue</strong>.
          </p>
          <p className="text-xs text-fg-secondary mt-0.5">
            You've used {budget.tokensUsedLast60Min} of {budget.hourlyBudget} tokens in the last
            hour. This run is short by {budget.deficit}.{' '}
            {minutesUntil !== null
              ? `Refill in ~${minutesUntil} min.`
              : 'Refill ETA unknown (no live balance).'}
          </p>
        </div>
      </div>
    </Card>
  );
}

function BrandSizeBadge({ status }: { status: BrandSizeStatus | null }) {
  if (status === null) return <span className="text-fg-tertiary">—</span>;
  if (status === 'pending') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-fg-tertiary"
        title="Brand stats loading…"
      >
        <RefreshCw className="size-3 animate-spin" />
        sizing
      </span>
    );
  }
  // Bucket-to-variant mapping. Micro/small are the wholesale sweet spots,
  // medium is borderline, large is the wholesale-unfriendly bucket users
  // typically want to filter out.
  const variant: 'positive' | 'accent' | 'warning' | 'neutral' =
    status === 'micro'
      ? 'positive'
      : status === 'small'
        ? 'accent'
        : status === 'medium'
          ? 'warning'
          : 'neutral';
  return (
    <Badge variant={variant} className="text-xs capitalize">
      {status}
    </Badge>
  );
}

function KeepaBalanceChip({
  tokensLeft,
  refillRate,
  loading,
  onRefresh,
}: {
  tokensLeft: number | null;
  refillRate: number | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  // Tier inference mirrors the server's keepa-tier helper (refillRate < 10
  // ≈ Keepa free tier, which caps balance at ~60). Matters because free-tier
  // accounts can't run default scouts; the badge tells the user upfront.
  const isFreeTier = refillRate !== null && refillRate < 10;
  return (
    <button
      type="button"
      onClick={onRefresh}
      title={
        isFreeTier
          ? `Keepa free tier — small searches only (cap ${tokensLeft ?? '?'}, refill ${refillRate}/min). Click to refresh.`
          : 'Click to refresh Keepa balance'
      }
      className="inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1 text-xs font-medium tabular-nums bg-surface-elev border border-border-subtle hover:border-border-default transition-colors duration-fast"
    >
      <Coins className="size-3.5 text-fg-tertiary" />
      <span className="text-fg-secondary">
        {tokensLeft === null ? '—' : tokensLeft.toLocaleString()}
      </span>
      <span className="text-fg-tertiary">tokens</span>
      {isFreeTier && (
        <Badge variant="warning" className="ml-1 text-[10px] px-1 py-0">
          Free tier
        </Badge>
      )}
      <RefreshCw
        className={`size-3 text-fg-tertiary ${loading ? 'animate-spin' : ''}`}
        aria-hidden
      />
    </button>
  );
}

function TavilyBalanceChip({
  usage,
  limit,
  remaining,
  plan,
  loading,
  onRefresh,
}: {
  usage: number;
  limit: number | null;
  remaining: number | null;
  plan: string;
  loading: boolean;
  onRefresh: () => void;
}) {
  // Display: when there's a key-level limit show "remaining / limit"; on
  // unlimited / paygo plans show "X used" instead. Hovering tells you which
  // plan you're on so a paygo user understands why no cap appears.
  const display =
    limit !== null && remaining !== null
      ? `${remaining.toLocaleString()} / ${limit.toLocaleString()}`
      : `${usage.toLocaleString()} used`;

  return (
    <button
      type="button"
      onClick={onRefresh}
      title={`Tavily plan: ${plan}. Click to refresh.`}
      className="inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1 text-xs font-medium tabular-nums bg-surface-elev border border-border-subtle hover:border-border-default transition-colors duration-fast"
    >
      <Globe className="size-3.5 text-fg-tertiary" />
      <span className="text-fg-secondary">{display}</span>
      <span className="text-fg-tertiary">tavily</span>
      <RefreshCw
        className={`size-3 text-fg-tertiary ${loading ? 'animate-spin' : ''}`}
        aria-hidden
      />
    </button>
  );
}

function QueryTextarea({
  value,
  onChange,
  disabled,
  onSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  onSubmit: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-resize as the user types.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [value]);

  return (
    <div className="relative">
      <Sparkles className="absolute left-3 top-3.5 size-5 text-fg-tertiary pointer-events-none" />
      <textarea
        ref={ref}
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        disabled={disabled}
        autoFocus
        spellCheck={false}
        placeholder='e.g. "Home & Kitchen gadgets under $50, BSR under 50K, fewer than 200 reviews, no Amazon as seller"'
        className="w-full pl-10 pr-4 py-3 text-base bg-surface-elev border border-border-default rounded-md placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed resize-none transition-colors duration-fast"
      />
    </div>
  );
}

function PreviewConfirmCard({
  filters,
  rationale,
  estimatedTokens,
  balance,
  tierHint,
  onRun,
  onQueue,
  onCancel,
  onRemoveFilter,
  /** Rendered "Save as template" trigger. Parent owns the SaveQueryDialog. */
  saveAction,
  /** Rendered "Save & start hourly" trigger. Disabled when hourly cap hit. */
  saveAndScheduleAction,
  runPending,
  queuePending,
}: {
  filters: KeepaFilters;
  rationale: string | null;
  estimatedTokens: number;
  balance: number | null;
  tierHint: 'free' | 'paid' | 'unknown';
  /** Run inline now (spends Keepa tokens immediately). */
  onRun: () => void;
  /** Defer to the next sweep tick (no immediate Keepa spend). */
  onQueue: () => void;
  onCancel: () => void;
  onRemoveFilter: (key: keyof KeepaFilters) => void;
  saveAction?: React.ReactNode;
  saveAndScheduleAction?: React.ReactNode;
  runPending?: boolean;
  queuePending?: boolean;
}) {
  const insufficient = balance !== null && estimatedTokens > balance;
  const tight = balance !== null && estimatedTokens > balance * 0.5 && !insufficient;

  return (
    <Card
      className={
        insufficient
          ? 'border-negative/30 bg-negative/[0.04]'
          : tight
            ? 'border-warning/30 bg-warning/[0.04]'
            : ''
      }
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wider text-fg-tertiary">
              AI parsed (preview — no Keepa tokens used yet)
            </p>
            {rationale && <p className="text-sm text-fg-secondary italic">{rationale}</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel} className="shrink-0">
            Cancel
          </Button>
        </div>

        <FilterChips filters={filters} rationale={null} onRemove={onRemoveFilter} />

        {tierHint === 'free' && (
          <p className="text-xs text-fg-tertiary">
            Keepa free-tier mode: limit auto-capped at 10 to fit the ~60-token balance ceiling.
            Upgrade Keepa for larger searches.
          </p>
        )}
        {insufficient && (
          <p className="text-xs text-negative">
            Not enough Keepa tokens ({balance} left, need ~{estimatedTokens}). Wait for refill, or
            narrow the query — remove filters to lower the limit, or set BSR/price caps. Or use
            <strong> Queue for later</strong> below to defer until the bucket refills.
          </p>
        )}
        {tight && (
          <p className="text-xs text-warning">
            This will use roughly {Math.round((estimatedTokens / (balance ?? 1)) * 100)}% of your
            balance ({balance} tokens left). Consider narrowing or using{' '}
            <strong>Queue for later</strong> instead.
          </p>
        )}

        {/*
         * Grouped action layout per docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3.
         * Two groups, each labeled with its own header so the verbs do work:
         *   1. Run this query — Run now + Queue for later
         *   2. Save for later — Save as template + Save & start hourly
         * Cancel sits at the card's top-right (above) to keep destructive
         * action separate from the constructive action grid.
         */}
        <div className="space-y-3 pt-1 border-t border-border-subtle">
          <div className="space-y-1.5 pt-2">
            <p className="text-[10px] uppercase tracking-wider text-fg-tertiary">Run this query</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={onRun}
                leadingIcon={<Play className="size-4" />}
                disabled={insufficient}
                loading={runPending}
                title={
                  insufficient
                    ? `Need ${estimatedTokens} tokens; have ${balance}. Try Queue for later.`
                    : 'Spend the estimated Keepa tokens and run the search'
                }
              >
                Run search · ~{estimatedTokens} tokens
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={onQueue}
                loading={queuePending}
                leadingIcon={<Clock className="size-4" />}
                title="Defers this run. A background sweep picks it up next time the bucket has budget."
              >
                Queue for later
              </Button>
            </div>
          </div>

          {(saveAction || saveAndScheduleAction) && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                Or — save for later (zero Keepa cost)
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {saveAction}
                {saveAndScheduleAction}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function EmptyResultDiagnostic({
  filters,
  rationale,
  totalResults,
  brandSizeFilteredCount,
  onRemove,
}: {
  filters: KeepaFilters;
  rationale: string | null;
  totalResults: number | null;
  brandSizeFilteredCount: number;
  onRemove: (key: keyof KeepaFilters) => void;
}) {
  // Pick the chips most likely to be over-restrictive — try removing them
  // first. Rough heuristic: review-count caps, BSR caps, monthly-sold floors,
  // brand exclusions, and limit. Keep things terse; one click should help.
  const suggestions: Array<{ key: keyof KeepaFilters; reason: string }> = [];
  if (filters.reviewCount?.max !== undefined)
    suggestions.push({
      key: 'reviewCount',
      reason: `cap of ${filters.reviewCount.max} reviews is restrictive`,
    });
  if (filters.bsrCurrent?.max !== undefined && filters.bsrCurrent.max < 100000)
    suggestions.push({
      key: 'bsrCurrent',
      reason: `BSR ≤ ${filters.bsrCurrent.max.toLocaleString()} is narrow`,
    });
  if (filters.monthlySoldEstimate?.min !== undefined)
    suggestions.push({
      key: 'monthlySoldEstimate',
      reason: `requiring ${filters.monthlySoldEstimate.min}+ monthly sales is strict`,
    });
  if (filters.amazonInStock === false)
    suggestions.push({
      key: 'amazonInStock',
      reason: 'excluding Amazon-as-seller cuts ~30% of catalog',
    });
  if (filters.brand?.length)
    suggestions.push({
      key: 'brand',
      reason: `restricted to brand: ${filters.brand.join(', ')}`,
    });
  if (filters.rating?.min !== undefined && filters.rating.min >= 4)
    suggestions.push({
      key: 'rating',
      reason: `${filters.rating.min}+ star floor excludes most listings`,
    });

  const interpretedTotal =
    totalResults === null ? 'unknown' : totalResults === 0 ? 'zero' : totalResults.toLocaleString();

  return (
    <Card className="border-warning/30 bg-warning/[0.04]">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-fg-primary">No results matched.</p>
          <p className="text-sm text-fg-secondary mt-1">
            Keepa reports <span className="font-mono tabular-nums">{interpretedTotal}</span>{' '}
            products matching your filters.{' '}
            {totalResults === 0
              ? 'The constraint set is too tight — at least one filter has to relax.'
              : brandSizeFilteredCount > 0
                ? `All ${brandSizeFilteredCount} fetched ${
                    brandSizeFilteredCount === 1 ? 'product was' : 'products were'
                  } from brands above your workspace's size cap, so none are shown. Raise the cap in Settings, or widen the query.`
                : 'Some matches exist but failed the per-product fetch (rare; usually a Keepa transient).'}
          </p>
          {rationale && (
            <p className="text-xs text-fg-tertiary italic mt-2">AI rationale: {rationale}</p>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-fg-tertiary mb-2">
            Filters that were sent (click any chip to remove + re-run)
          </p>
          <FilterChips filters={filters} rationale={null} onRemove={onRemove} />
        </div>

        {suggestions.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wider text-fg-tertiary">
              Likely culprits — try removing one:
            </p>
            <ul className="space-y-1">
              {suggestions.slice(0, 3).map((s) => (
                <li key={s.key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-fg-secondary">{s.reason}</span>
                  <Button variant="secondary" size="sm" onClick={() => onRemove(s.key)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function DropdownLink({ to, label, subtitle }: { to: string; label: string; subtitle?: string }) {
  return (
    <Link to={to} className="block px-3 py-2 text-sm text-fg-primary hover:bg-surface">
      <div>{label}</div>
      {subtitle && <div className="text-xs text-fg-tertiary truncate">{subtitle}</div>}
    </Link>
  );
}

function SortHeader({
  label,
  field,
  sort,
  onClick,
  align,
}: {
  label: string;
  field: SortField;
  sort: { field: SortField; dir: SortDir };
  onClick: (f: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.field === field;
  return (
    <th
      className={`px-4 py-2.5 font-medium cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onClick(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sort.dir === 'asc' ? (
            <ArrowUpAZ className="size-3" />
          ) : (
            <ArrowDownAZ className="size-3" />
          ))}
      </span>
    </th>
  );
}

function FilterChips({
  filters,
  rationale,
  onRemove,
}: {
  filters: KeepaFilters;
  rationale: string | null;
  onRemove: (key: keyof KeepaFilters) => void;
}) {
  const chips: Array<{ key: keyof KeepaFilters; label: string }> = [];
  if (filters.category) chips.push({ key: 'category', label: filters.category });
  if (filters.bsrCurrent)
    chips.push({ key: 'bsrCurrent', label: rangeLabel('BSR', filters.bsrCurrent) });
  if (filters.amazonPriceCurrent)
    chips.push({ key: 'amazonPriceCurrent', label: priceRangeLabel(filters.amazonPriceCurrent) });
  if (filters.reviewCount)
    chips.push({ key: 'reviewCount', label: rangeLabel('reviews', filters.reviewCount) });
  if (filters.rating) chips.push({ key: 'rating', label: rangeLabel('rating', filters.rating) });
  if (filters.monthlySoldEstimate)
    chips.push({
      key: 'monthlySoldEstimate',
      label: rangeLabel('sales/mo', filters.monthlySoldEstimate),
    });
  if (filters.brand?.length)
    chips.push({ key: 'brand', label: `brand: ${filters.brand.join(', ')}` });
  if (filters.excludeBrand?.length)
    chips.push({ key: 'excludeBrand', label: `not: ${filters.excludeBrand.join(', ')}` });
  if (filters.amazonInStock !== undefined)
    chips.push({
      key: 'amazonInStock',
      label: filters.amazonInStock ? 'Amazon selling' : 'Amazon NOT selling',
    });
  if (filters.title?.contains)
    chips.push({ key: 'title', label: `title contains: ${filters.title.contains}` });
  if (filters.limit !== undefined) chips.push({ key: 'limit', label: `limit: ${filters.limit}` });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-wider text-fg-tertiary">Filters:</span>
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className="inline-flex items-center gap-1 rounded-xs px-2 py-0.5 text-xs font-medium tabular-nums whitespace-nowrap bg-accent/[0.12] text-accent-fg hover:bg-accent/[0.2] transition-colors duration-fast"
            onClick={() => onRemove(chip.key)}
            title="Remove this filter"
          >
            {chip.label}
            <X className="size-3" />
          </button>
        ))}
      </div>
      {rationale && <p className="text-xs text-fg-tertiary italic">{rationale}</p>}
    </div>
  );
}

function rangeLabel(prefix: string, range: { min?: number; max?: number }): string {
  if (range.min !== undefined && range.max !== undefined)
    return `${prefix}: ${range.min}–${range.max}`;
  if (range.max !== undefined) return `${prefix} ≤ ${range.max}`;
  if (range.min !== undefined) return `${prefix} ≥ ${range.min}`;
  return prefix;
}

function priceRangeLabel(range: { min?: number; max?: number }): string {
  const fmt = (cents: number) => `$${(cents / 100).toFixed(0)}`;
  if (range.min !== undefined && range.max !== undefined)
    return `price: ${fmt(range.min)}–${fmt(range.max)}`;
  if (range.max !== undefined) return `price ≤ ${fmt(range.max)}`;
  if (range.min !== undefined) return `price ≥ ${fmt(range.min)}`;
  return 'price';
}

function DiscoveredByTemplateAccordion({
  template,
  currentRunAsins,
}: {
  template: KeepaQueryTemplate;
  currentRunAsins: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'interested' | 'pursuing' | 'passed'
  >('all');
  const [sortBy, setSortBy] = useState<'recent' | 'roi' | 'bsr' | 'price'>('recent');

  const params = {
    sortBy,
    sortOrder: 'desc' as const,
    limit: 100,
    ...(statusFilter === 'all'
      ? { hidePassed: false as const }
      : { status: statusFilter, hidePassed: false as const }),
  };
  const discovered = useTemplateDiscoveredProducts(open ? template.id : null, params);
  const total = discovered.data?.total ?? null;
  const items = discovered.data?.items ?? [];
  // Hide products in the just-run results table from the accordion so we don't
  // double-show them. Operator's mental model: this list is "everything ELSE
  // this template has surfaced over time."
  const filtered = items.filter((p) => !currentRunAsins.has(p.asin));

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-md border border-border-subtle bg-surface"
    >
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-fg-primary hover:bg-surface-elev">
        <span className="inline-flex items-center gap-2">
          <ChevronDown
            className={`size-4 text-fg-tertiary transition-transform duration-fast ${
              open ? 'rotate-0' : '-rotate-90'
            }`}
          />
          All discovered by this query
          {total !== null && (
            <span className="text-fg-tertiary font-normal">
              · {total.toLocaleString()} total ASINs
            </span>
          )}
        </span>
      </summary>
      {open && (
        <div className="border-t border-border-subtle p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-fg-tertiary">Status:</span>
            {(['all', 'pending', 'interested', 'pursuing', 'passed'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-2 py-0.5 text-xs font-medium rounded-xs transition-colors duration-fast ${
                  statusFilter === s
                    ? 'bg-accent/[0.18] text-accent-fg'
                    : 'text-fg-secondary hover:text-fg-primary border border-border-subtle'
                }`}
              >
                {s}
              </button>
            ))}
            <span className="ml-3 text-xs uppercase tracking-wider text-fg-tertiary">Sort:</span>
            {(['recent', 'roi', 'bsr', 'price'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSortBy(s)}
                className={`px-2 py-0.5 text-xs font-medium rounded-xs transition-colors duration-fast ${
                  sortBy === s
                    ? 'bg-accent/[0.18] text-accent-fg'
                    : 'text-fg-secondary hover:text-fg-primary border border-border-subtle'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {discovered.isLoading && (
            <p className="text-sm text-fg-tertiary">Loading discovered products…</p>
          )}
          {discovered.isError && (
            <p className="text-sm text-negative">
              Failed to load: {(discovered.error as Error)?.message}
            </p>
          )}

          {discovered.data && filtered.length === 0 && (
            <p className="text-sm text-fg-tertiary">
              {items.length === 0
                ? 'No products discovered by this query yet — schedule it to find more over time.'
                : 'All products discovered by this query are already in the current results above.'}
            </p>
          )}

          {filtered.length > 0 && (
            <div className="border border-border-subtle rounded-md bg-canvas overflow-hidden">
              <table className="w-full text-sm tabular-nums">
                <thead className="border-b border-border-subtle bg-surface-elev">
                  <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                    <th className="text-left px-3 py-2 font-medium">ASIN</th>
                    <th className="text-left px-3 py-2 font-medium">Title</th>
                    <th className="text-left px-3 py-2 font-medium">Brand</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-right px-3 py-2 font-medium">BSR</th>
                    <th className="text-right px-3 py-2 font-medium">Price</th>
                    <th className="text-right px-3 py-2 font-medium">ROI</th>
                    <th className="text-center px-3 py-2 font-medium">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-t border-border-subtle hover:bg-surface-elev">
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link
                          to="/products/$asin"
                          params={{ asin: p.asin }}
                          className="hover:underline"
                        >
                          {p.asin}
                        </Link>
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate text-fg-secondary">
                        {p.title ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-fg-secondary">{p.brand ?? '—'}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.currentBsr === null ? '—' : p.currentBsr.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.currentPriceCents === null
                          ? '—'
                          : `$${(p.currentPriceCents / 100).toFixed(2)}`}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.estimatedRoiPct === null
                          ? '—'
                          : `${(p.estimatedRoiPct * 100).toFixed(0)}%`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.roiGrade ? <GradeBadge grade={p.roiGrade} /> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </details>
  );
}

/**
 * Clickable brand chip in the results table. Renders as plain text when the
 * brand is null (no brand info from Keepa for that ASIN). Otherwise it's a
 * button that opens the brand drill-down dialog. The dialog only fetches
 * brand details when actually opened — avoids N parallel /api/brands calls
 * across a results table of 10–50 rows.
 */
function BrandChip({ brand }: { brand: string | null }) {
  const [open, setOpen] = useState(false);
  if (!brand) return <>—</>;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-fg-primary hover:text-accent-fg hover:underline cursor-pointer text-left"
        title={`Show catalog footprint for ${brand}`}
      >
        {brand}
      </button>
      {open && <BrandSummaryDialog brand={brand} open={open} onOpenChange={setOpen} />}
    </>
  );
}

function StatusBadge({ status }: { status: 'pending' | 'interested' | 'pursuing' | 'passed' }) {
  const variant: 'neutral' | 'accent' | 'positive' | 'warning' =
    status === 'interested'
      ? 'accent'
      : status === 'pursuing'
        ? 'positive'
        : status === 'passed'
          ? 'neutral'
          : 'warning';
  return (
    <Badge variant={variant} className="text-xs capitalize">
      {status}
    </Badge>
  );
}

function GradeBadge({ grade }: { grade: 'A' | 'B' | 'C' | 'D' | 'F' }) {
  const variant =
    grade === 'A' || grade === 'B' ? 'positive' : grade === 'C' ? 'warning' : 'negative';
  return <Badge variant={variant}>{grade}</Badge>;
}

/**
 * Client-side mirror of the server's keepa-cost estimator. Used after a chip
 * removal in the preview state so the estimate updates instantly without a
 * round-trip. Server's estimate is authoritative on actual run.
 */
function estimateClientCost(filters: KeepaFilters): number {
  const limit = filters.limit ?? 25;
  const capped = Math.min(limit, 100);
  return 5 + capped * 4;
}
